import React, { useEffect, useRef, useState } from 'react';
import UiIcon from '../../common/UiIcon';

interface CardSelectionPageProps {
  selectedCount?: number;
  maxCards?: number;
  persistKey?: string;
  onCardSelect?: (cardIndex: number) => void;
  onRevealFate?: () => void | Promise<void>;
  isRevealLoading?: boolean;
  revealProgress?: number;
}

const TOTAL_CARDS = 78;
const DESKTOP_CARD_SPACING = 42;
const MOBILE_CARD_SPACING = 34;

type AnimationPhase = 'shuffling' | 'gathering' | 'spreading';

function getAllStoppedCards(): Set<number> {
  return new Set(Array.from({ length: TOTAL_CARDS }, (_, index) => index));
}

function getSpreadCtaLabel(maxCards: number): string {
  if (maxCards === 1) {
    return '이 한 장의 답 읽기';
  }

  if (maxCards === 3) {
    return '세 장의 흐름 읽기';
  }

  if (maxCards === 10) {
    return '열 장의 세션 열기';
  }

  return `${maxCards}장의 흐름 읽기`;
}

function getCardInstruction(maxCards: number): string {
  if (maxCards === 1) {
    return '가장 먼저 시선이 머무는 한 장을 골라 주세요.';
  }

  if (maxCards === 3) {
    return '왼쪽에서 오른쪽으로, 시작점부터 마지막 흐름까지 골라 주세요.';
  }

  if (maxCards === 10) {
    return '현재, 감정, 흐름이 차례로 이어지도록 카드 열 장을 선택해 주세요.';
  }

  return '카드를 차례대로 골라 지금 질문의 흐름을 펼쳐 주세요.';
}

function getSelectionSlots(maxCards: number): string[] {
  if (maxCards === 1) {
    return ['핵심'];
  }

  if (maxCards === 3) {
    return ['시작점', '핵심', '향하는 방향'];
  }

  if (maxCards === 10) {
    return [
      '현재 상황',
      '걸림돌',
      '의식하는 목표',
      '바닥 감정',
      '최근 흐름',
      '가까운 전개',
      '내 상태',
      '주변 영향',
      '두려움과 기대',
      '가장 유력한 결말',
    ];
  }

  return Array.from({ length: maxCards }, (_, index) => `${index + 1}번째 카드`);
}

function getCardLabel(index: number, selectedCards: number[]): string {
  const cardNumber = index + 1;
  const selectedIndex = selectedCards.indexOf(index);

  if (selectedIndex >= 0) {
    return `카드 ${cardNumber}, ${selectedIndex + 1}번째로 선택됨. 다시 누르면 선택이 해제됩니다.`;
  }

  return `카드 ${cardNumber}, 선택하려면 누르세요.`;
}

function getCardSpacing(): number {
  if (typeof window !== 'undefined' && window.innerWidth < 768) {
    return MOBILE_CARD_SPACING;
  }

  return DESKTOP_CARD_SPACING;
}

const CardSelectionPage: React.FC<CardSelectionPageProps> = ({
  selectedCount,
  maxCards = 3,
  persistKey,
  onCardSelect,
  onRevealFate,
  isRevealLoading = false,
  revealProgress = 0,
}) => {
  const [selectedCards, setSelectedCards] = useState<number[]>([]);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>('shuffling');
  const [shuffleIteration, setShuffleIteration] = useState(0);
  const [stoppedCards, setStoppedCards] = useState<Set<number>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  const displayedSelectedCount = selectedCount ?? selectedCards.length;
  const selectionSlots = getSelectionSlots(maxCards);
  const nextSlot = selectionSlots[displayedSelectedCount];

  const getMaxScrollOffset = () => {
    const cardSpacing = getCardSpacing();
    const totalWidth = (TOTAL_CARDS - 1) * cardSpacing;
    const viewportWidth = containerRef.current?.clientWidth
      ?? (typeof window !== 'undefined' ? window.innerWidth : 1280);

    return Math.max(320, totalWidth / 2 - viewportWidth / 2 + 120);
  };

  const clampScrollOffset = (value: number) => {
    const maxOffset = getMaxScrollOffset();
    return Math.max(-maxOffset, Math.min(maxOffset, value));
  };

  useEffect(() => {
    if (!persistKey || typeof window === 'undefined') {
      return;
    }

    try {
      const raw = window.sessionStorage.getItem(persistKey);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw) as number[];
      if (!Array.isArray(parsed)) {
        return;
      }

      const restored = parsed
        .filter((value) => Number.isInteger(value) && value >= 0 && value < TOTAL_CARDS)
        .slice(0, maxCards);

      setSelectedCards(restored);
    } catch {
      // noop
    }
  }, [persistKey, maxCards]);

  useEffect(() => {
    if (!persistKey || typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem(persistKey, JSON.stringify(selectedCards));
  }, [persistKey, selectedCards]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const hasRestorableSelection = (() => {
      if (!persistKey) {
        return false;
      }

      try {
        const raw = window.sessionStorage.getItem(persistKey);
        if (!raw) {
          return false;
        }

        const parsed = JSON.parse(raw) as number[];
        if (!Array.isArray(parsed)) {
          return false;
        }

        const restored = parsed
          .filter((value) => Number.isInteger(value) && value >= 0 && value < TOTAL_CARDS)
          .slice(0, maxCards);

        return restored.length > 0;
      } catch {
        return false;
      }
    })();

    if (hasRestorableSelection) {
      setShuffleIteration(7);
      setAnimationPhase('spreading');
      setStoppedCards(getAllStoppedCards());
      return;
    }

    const shuffleTimers: number[] = [];

    for (let index = 1; index <= 7; index += 1) {
      shuffleTimers.push(
        window.setTimeout(() => {
          setShuffleIteration(index);
        }, index * 200),
      );
    }

    const gatheringTimer = window.setTimeout(() => {
      setAnimationPhase('gathering');
    }, 1600);

    const spreadingTimer = window.setTimeout(() => {
      setAnimationPhase('spreading');
      for (let index = 0; index < TOTAL_CARDS; index += 1) {
        window.setTimeout(() => {
          setStoppedCards((prev) => new Set([...prev, index]));
        }, index * 25);
      }
    }, 2200);

    return () => {
      shuffleTimers.forEach(window.clearTimeout);
      window.clearTimeout(gatheringTimer);
      window.clearTimeout(spreadingTimer);
    };
  }, [persistKey, maxCards]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      setScrollOffset((prev) => clampScrollOffset(prev));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCardClick = (cardIndex: number) => {
    if (animationPhase !== 'spreading' || isRevealLoading) {
      return;
    }

    if (selectedCards.includes(cardIndex)) {
      setSelectedCards((prev) => prev.filter((value) => value !== cardIndex));
      return;
    }

    if (selectedCards.length < maxCards) {
      setSelectedCards((prev) => [...prev, cardIndex]);
      onCardSelect?.(cardIndex);
    }
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (animationPhase !== 'spreading') {
      return;
    }

    setIsDragging(true);
    setStartX(event.clientX - scrollOffset);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isDragging) {
      return;
    }

    const nextOffset = event.clientX - startX;
    setScrollOffset(clampScrollOffset(nextOffset));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    if (animationPhase !== 'spreading') {
      return;
    }

    setIsDragging(true);
    setStartX(event.touches[0].clientX - scrollOffset);
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (!isDragging) {
      return;
    }

    const nextOffset = event.touches[0].clientX - startX;
    setScrollOffset(clampScrollOffset(nextOffset));
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const getShuffleTransform = (index: number, iteration: number) => {
    const seed1 = ((index * 12345 + iteration * 11111 + 67890) % 10000) / 10000;
    const seed2 = ((index * 54321 + iteration * 22222 + 9876) % 10000) / 10000;
    const seed3 = ((index * 24680 + iteration * 33333 + 13579) % 10000) / 10000;
    const seed4 = ((index * 86420 + iteration * 44444 + 97531) % 10000) / 10000;

    return {
      x: (seed1 - 0.5) * 500,
      y: (seed2 - 0.5) * 250,
      rotation: (seed3 - 0.5) * 180,
      scale: 0.8 + seed4 * 0.4,
    };
  };

  const getCardTransform = (index: number) => {
    const cardSpacing = getCardSpacing();
    const totalWidth = (TOTAL_CARDS - 1) * cardSpacing;
    const baseX = index * cardSpacing - totalWidth / 2 + scrollOffset;
    const centerIndex = TOTAL_CARDS / 2;
    const distanceFromCenter = index - centerIndex;
    const normalizedDistance = distanceFromCenter / centerIndex;
    const arcHeight = 100;
    const y = Math.abs(normalizedDistance) * Math.abs(normalizedDistance) * arcHeight;
    const maxRotation = 5;
    const rotation = normalizedDistance * maxRotation;
    const scale = 1 - Math.abs(normalizedDistance) * 0.12;
    const isSelected = selectedCards.includes(index);
    const isHovered = hoveredCard === index;

    let offsetY = 0;

    if (isSelected) {
      offsetY = -50;
    } else if (isHovered) {
      offsetY = -25;
    }

    return {
      x: baseX,
      y: y + offsetY,
      rotation,
      scale,
      zIndex: index,
    };
  };

  const getGatheringTransform = () => {
    const leftmostCard = getCardTransform(0);

    return {
      x: leftmostCard.x,
      y: leftmostCard.y,
      rotation: leftmostCard.rotation,
      scale: leftmostCard.scale,
      zIndex: 0,
    };
  };

  const safeProgress = Math.max(0, Math.min(100, Math.round(revealProgress)));
  const readyToPick = animationPhase === 'spreading' && stoppedCards.size === TOTAL_CARDS;

  const title = animationPhase === 'shuffling'
    ? '카드의 결을 섞고 있어요'
    : animationPhase === 'gathering'
      ? '질문의 숨을 고르고 있어요'
      : maxCards === 1
        ? '한 장을 골라 주세요'
        : `${maxCards}장을 골라 주세요`;

  return (
    <div
      className="relative flex h-screen flex-col overflow-hidden font-serif text-white selection:bg-[#bd3b0f] selection:text-white"
      style={{
        background: 'radial-gradient(circle at 50% 30%, #2a221e 0%, #14110f 70%, #000000 100%)',
      }}
    >
      <div className="pointer-events-none fixed inset-0 z-50 bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22 opacity=%220.04%22/%3E%3C/svg%3E')] opacity-[0.08]" />

      <header className="absolute left-0 top-0 z-40 flex w-full items-center justify-between p-6 opacity-80 transition-opacity duration-300 hover:opacity-100 md:p-8">
        <div className="flex items-center gap-3">
          <UiIcon name="book" className="h-6 w-6 text-[#fbbf24]" />
          <span className="text-sm font-bold uppercase tracking-[0.3em] text-[#c9a092]">
            Myoan Tarot
          </span>
        </div>
        <div className="text-right">
          <div className="text-sm text-[#c9a092]">{displayedSelectedCount}/{maxCards}</div>
          {readyToPick && nextSlot && (
            <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-[#fbbf24]/70">
              다음 자리: {nextSlot}
            </p>
          )}
        </div>
      </header>

      <main className="relative z-10 flex flex-grow flex-col items-center justify-center px-4 pb-28 pt-20 md:pb-32">
        <div className="z-30 mb-8 text-center">
          <h1 className="font-serif text-2xl font-extrabold leading-tight tracking-[-0.01em] text-[#ead6bd] md:text-4xl">
            {title}
          </h1>
          {readyToPick && (
            <>
              <p className="mt-2 flex items-center justify-center gap-2 text-[10px] font-medium uppercase tracking-[0.4em] text-[#fbbf24] opacity-80 md:text-xs">
                <span className="h-[1px] w-8 bg-[#fbbf24]/50" />
                묘안이 질문의 결을 열 준비를 마쳤어요
                <span className="h-[1px] w-8 bg-[#fbbf24]/50" />
              </p>
              <p className="mt-2 text-xs text-[#c9a092]/60">
                {getCardInstruction(maxCards)}
              </p>
              <div className="mt-4 flex max-w-4xl flex-wrap items-center justify-center gap-2 px-2">
                {selectionSlots.map((slot, index) => {
                  const isDone = index < displayedSelectedCount;
                  const isActive = index === displayedSelectedCount && displayedSelectedCount < maxCards;

                  return (
                    <div
                      key={slot}
                      className={`border px-3 py-2 text-[10px] uppercase tracking-[0.16em] md:text-[11px] ${
                        isDone
                          ? 'border-[#fbbf24]/45 bg-[#2b1c14]/92 text-[#f8e2bf]'
                          : isActive
                            ? 'border-[#fbbf24]/28 bg-[#1c1512]/90 text-[#fbbf24]/88'
                            : 'border-[#5c4638]/38 bg-[#161210]/82 text-[#c9a092]/60'
                      }`}
                    >
                      {index + 1}. {slot}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div
          ref={containerRef}
          className={`relative flex h-[400px] w-full max-w-6xl items-center justify-center ${
            !readyToPick ? 'cursor-wait' : isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {readyToPick && (
            <div className="pointer-events-none absolute bottom-8 left-1/2 z-[55] flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full border-2 border-[#fbbf24]/30 bg-[#fbbf24]/10">
              <UiIcon name="eye" className="h-5 w-5 text-[#fbbf24]" />
            </div>
          )}

          <div className="absolute left-1/2 top-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center">
            {Array.from({ length: TOTAL_CARDS }).map((_, index) => {
              const isSelected = selectedCards.includes(index);
              const shuffleTransform = getShuffleTransform(index, shuffleIteration);
              const gatheringTransform = getGatheringTransform();
              const fanTransform = getCardTransform(index);

              let transform = shuffleTransform;
              let transitionStyle = 'all 0.2s ease-in-out';

              if (animationPhase === 'gathering') {
                transform = gatheringTransform;
                transitionStyle = 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)';
              } else if (animationPhase === 'spreading') {
                transform = stoppedCards.has(index) ? fanTransform : getCardTransform(stoppedCards.size);
                transitionStyle = 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
              }

              return (
                <button
                  key={index}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCardClick(index);
                  }}
                  onMouseEnter={() => readyToPick && setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  aria-label={getCardLabel(index, selectedCards)}
                  aria-pressed={isSelected}
                  title={getCardLabel(index, selectedCards)}
                  className={`absolute h-24 w-16 rounded-xl border-2 md:h-32 md:w-20 ${
                    !readyToPick ? 'pointer-events-none cursor-wait' : 'cursor-pointer'
                  } ${
                    isSelected
                      ? 'border-[#c79b66] bg-[#6d4a24]/35'
                      : 'border-[#3d2e24] bg-[#1a1816] hover:border-[#fbbf24]'
                  }`}
                  style={{
                    transform: `translate(${transform.x}px, ${transform.y}px) rotate(${transform.rotation}deg) scale(${transform.scale})`,
                    zIndex: animationPhase === 'spreading' && stoppedCards.has(index) ? fanTransform.zIndex : index,
                    transition: transitionStyle,
                  }}
                >
                  <div className="pointer-events-none absolute inset-1 flex items-center justify-center rounded-lg border border-dashed border-white/5 opacity-60">
                    <UiIcon
                      name="sparkles"
                      className={`h-6 w-6 transition-[color,transform] duration-500 md:h-7 md:w-7 ${
                        isSelected ? 'scale-110 text-[#fbbf24]' : 'text-[#fbbf24]/30'
                      }`}
                    />
                  </div>
                  <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-black/60 to-transparent" aria-hidden="true" />
                  {isSelected && (
                    <div
                      className="pointer-events-none absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-[#fbbf24] text-[10px] font-bold text-black"
                      aria-hidden="true"
                    >
                      {selectedCards.indexOf(index) + 1}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {readyToPick && (
          <div className={`mt-4 text-center text-sm text-[#c9a092]/70 ${selectedCards.length === maxCards ? 'mb-20 md:mb-24' : ''}`}>
            <p>드래그해서 좌우로 움직이고, 마음이 멈추는 카드를 눌러 선택해 주세요.</p>
            {nextSlot && (
              <p className="mt-2 text-xs uppercase tracking-[0.16em] text-[#fbbf24]/65">
                지금 고르는 자리: {nextSlot}
              </p>
            )}
          </div>
        )}
      </main>

      {selectedCards.length === maxCards && (
        <div className="fixed bottom-10 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 animate-selection-fade-in md:bottom-14 md:w-auto md:max-w-none">
          <button
            type="button"
            onClick={onRevealFate}
            disabled={isRevealLoading}
            className="group flex w-full items-center justify-center gap-3 rounded-lg border border-[#c79b66]/55 bg-[#221510]/88 px-6 py-3 text-xs uppercase tracking-[0.2em] text-amber-100 transition-colors hover:border-[#d2aa78] hover:bg-[#2a1b12] hover:text-white md:w-auto"
          >
            <span className="cta-sigil" />
            {getSpreadCtaLabel(maxCards)}
          </button>
        </div>
      )}

      {isRevealLoading && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 px-6 backdrop-blur-sm">
          <div className="w-full max-w-md border border-[#fbbf24]/25 bg-[#1a1512]/95 p-6 shadow-2xl md:p-7">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#fbbf24]/80">
              <UiIcon name="sparkles" className="h-4 w-4" />
              <span>리딩 생성 중</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[#c9a092]/85">
              묘안이 카드의 순서와 의미를 이어 보고 있어요. 잠시만 기다려 주세요.
            </p>
            <div className="mt-5 h-2 w-full overflow-hidden border border-[#fbbf24]/20 bg-[#2a211c]">
              <div
                className="h-full bg-gradient-to-r from-[#bd3b0f] via-[#f59e0b] to-[#fbbf24] transition-[width] duration-300 ease-out"
                style={{ width: `${safeProgress}%` }}
              />
            </div>
            <div className="mt-2 text-right text-[11px] tracking-[0.16em] text-[#c9a092]/70">
              {safeProgress}%
            </div>
            <div className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#c9a092]/55">
              <span className="loading-dot" />
              <span className="loading-dot" style={{ animationDelay: '0.2s' }} />
              <span className="loading-dot" style={{ animationDelay: '0.4s' }} />
              <span>카드의 목소리를 정리하는 중</span>
            </div>
          </div>
        </div>
      )}

      <footer className="absolute bottom-0 z-40 w-full bg-transparent px-6 py-4">
        <div className="mx-auto flex max-w-7xl items-end justify-between">
          <div className="text-[10px] uppercase tracking-widest text-[#c9a092]/20">
            Myoan Tarot
          </div>
          <div className="flex items-center gap-2 text-[10px] font-light tracking-[0.2em] text-[#c9a092]/30">
            <UiIcon name="moon" className="h-4 w-4" />
            <span>묘안이 질문의 결을 읽고 있어요</span>
          </div>
        </div>
      </footer>

      <style>{`
        .cta-sigil {
          position: relative;
          width: 2rem;
          height: 1px;
          background: #fbbf24;
          opacity: 0.9;
          transition: transform 0.4s ease, opacity 0.4s ease;
        }
        .cta-sigil::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          width: 0.35rem;
          height: 0.35rem;
          border-radius: 9999px;
          background: #fbbf24;
          transform: translate(-2px, -50%);
          transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.45s ease;
        }
        .group:hover .cta-sigil {
          opacity: 1;
          transform: translateX(2px);
        }
        .group:hover .cta-sigil::after {
          transform: translate(6px, -50%);
          opacity: 0.95;
        }
        .loading-dot {
          display: inline-block;
          width: 0.3rem;
          height: 0.3rem;
          border-radius: 9999px;
          background: rgba(251, 191, 36, 0.7);
        }
        @keyframes selection-fade-in {
          from { opacity: 0; transform: translate(-50%, 10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        .animate-selection-fade-in {
          animation: selection-fade-in 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CardSelectionPage;
